import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Table, Spin, Alert, Button, Tag, Card, Modal, Form, Input, Select, message, Row, Col, Typography, Space, Empty, Tooltip, Checkbox, Popconfirm, Dropdown, Menu } from 'antd'; // Added Dropdown, Menu, Tooltip, Checkbox, Popconfirm
import MomentDatePicker from './MomentDatePicker.tsx';
import { PlusCircleOutlined, EditOutlined, FormOutlined, CheckCircleOutlined, HistoryOutlined, PlusOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons'; // Added MoreOutlined, HistoryOutlined, PlusOutlined, DeleteOutlined
import StructuredMedicationChecklistForm from './StructuredMedicationChecklistForm'; // Import the checklist form
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions'; // Added useAuth
import { useTranslation } from 'react-i18next'; // Added for translations
import { getTranslatedRoutes, getTranslatedFrequencies } from '../utils/medicationOptions'; // Import options

const { Option } = Select;
const { TextArea } = Input;
// Removed incorrect Checkbox import: const { Checkbox } = Form;
const { Text, Title } = Typography;

const PatientMedications = ({ patientId, triggerOpenAddModal, onAddModalClose, onMedicationAndOrderAdded, onMedicationUpdated, refreshTrigger }) => { // Added onMedicationUpdated, refreshTrigger
  const [medications, setMedications] = useState([]);
  const [structuredChecklist, setStructuredChecklist] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [addSubmitLoading, setAddSubmitLoading] = useState(false);
  const [editSubmitLoading, setEditSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false); // Added delete loading state
  const [error, setError] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isChecklistModalVisible, setIsChecklistModalVisible] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false); // Added for quick status update loading
  const [editingMedication, setEditingMedication] = useState(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [lastReviewed, setLastReviewed] = useState(null); // Added lastReviewed state
  const { user } = useAuth(); // Get user from context
  const { hasPermission } = usePermissions();
  const { t } = useTranslation(); // Add translation hook

  // State for conditional fields in modals
  const [isPrnCheckedAdd, setIsPrnCheckedAdd] = useState(false);
  const [isOtherFrequencyAdd, setIsOtherFrequencyAdd] = useState(false);
  const [isPrnCheckedEdit, setIsPrnCheckedEdit] = useState(false);
  const [isOtherFrequencyEdit, setIsOtherFrequencyEdit] = useState(false);

  // State for medication search
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Popular medications list for empty search
  const [popularMedications] = useState([
    { rxcui: 'popular_1', displayName: 'Metformin 500mg Tablet', medicationName: 'Metformin 500mg Tablet' },
    { rxcui: 'popular_2', displayName: 'Lisinopril 10mg Tablet', medicationName: 'Lisinopril 10mg Tablet' },
    { rxcui: 'popular_3', displayName: 'Amlodipine 5mg Tablet', medicationName: 'Amlodipine 5mg Tablet' },
    { rxcui: 'popular_4', displayName: 'Atorvastatin 20mg Tablet', medicationName: 'Atorvastatin 20mg Tablet' },
    { rxcui: 'popular_5', displayName: 'Omeprazole 20mg Capsule', medicationName: 'Omeprazole 20mg Capsule' },
    { rxcui: 'popular_6', displayName: 'Aspirin 81mg Tablet', medicationName: 'Aspirin 81mg Tablet' },
    { rxcui: 'popular_7', displayName: 'Losartan 50mg Tablet', medicationName: 'Losartan 50mg Tablet' },
    { rxcui: 'popular_8', displayName: 'Hydrochlorothiazide 25mg Tablet', medicationName: 'Hydrochlorothiazide 25mg Tablet' },
    { rxcui: 'popular_9', displayName: 'Gabapentin 300mg Capsule', medicationName: 'Gabapentin 300mg Capsule' },
    { rxcui: 'popular_10', displayName: 'Sertraline 50mg Tablet', medicationName: 'Sertraline 50mg Tablet' },
    { rxcui: 'popular_11', displayName: 'Levothyroxine 50mcg Tablet', medicationName: 'Levothyroxine 50mcg Tablet' },
    { rxcui: 'popular_12', displayName: 'Prednisone 5mg Tablet', medicationName: 'Prednisone 5mg Tablet' },
    { rxcui: 'popular_13', displayName: 'Ibuprofen 400mg Tablet', medicationName: 'Ibuprofen 400mg Tablet' },
    { rxcui: 'popular_14', displayName: 'Furosemide 40mg Tablet', medicationName: 'Furosemide 40mg Tablet' },
    { rxcui: 'popular_15', displayName: 'Warfarin 5mg Tablet', medicationName: 'Warfarin 5mg Tablet' }
  ]);

  // State for custom medication entry
  const [isCustomMedicationAdd, setIsCustomMedicationAdd] = useState(false);
  const [isCustomMedicationEdit, setIsCustomMedicationEdit] = useState(false);

  const { token } = useAuth(); // Get token for API calls

  // Debounce function
  const debounce = (func, delay) => {
    let timeout;
    return function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const handleMedicationSearch = async (query) => {
    // If no query or query is too short, show popular medications
    if (!query || query.length < 3) {
      setSearchResults(popularMedications);
      return;
    }

    // Search for medications with query
    setIsSearching(true);
    try {
      const response = await axios.get(`/medications/search?term=${query}`);
      const results = response.data.data?.medications?.map(med => ({
        rxcui: med.rxcui,
        medicationName: med.medicationName,
        displayName: med.displayName || med.medicationName,
        synonym: med.synonym,
        tty: med.tty
      })) || [];
      setSearchResults(results);
    } catch (error) {
      console.error('Failed to search for medications:', error);
      if (error.response?.status === 503) {
        message.warning(t('medications.searchServiceUnavailable'));
      } else if (error.response?.status === 408) {
        message.warning(t('medications.searchTimeout'));
      } else {
        message.error(t('medications.searchFailed'));
      }
      // On error, fall back to showing popular medications
      setSearchResults(popularMedications);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useCallback(debounce(handleMedicationSearch, 500), []);

  // Handler for when the medication search dropdown is opened/focused
  const handleMedicationFocus = () => {
    // Show popular medications if no search results are currently displayed
    if (searchResults.length === 0) {
      setSearchResults(popularMedications);
    }
  };

  // Helper function to map medication frequency to order frequency
  const mapMedicationFrequencyToOrderFrequency = (medicationFrequency) => {
    const frequencyMap = {
      'Once daily (QD)': 'QD',
      'Twice daily (BID)': 'BID',
      'Three times daily (TID)': 'TID',
      'Four times daily (QID)': 'QID',
      'Every morning (QAM)': 'Daily',
      'Every evening (QPM)': 'Daily',
      'Every 4 hours (Q4H)': 'QID', // Approximate mapping
      'Every 6 hours (Q6H)': 'QID', // Approximate mapping
      'Every 8 hours (Q8H)': 'TID', // Approximate mapping
      'Every 12 hours (Q12H)': 'BID', // Approximate mapping
      'At bedtime (HS)': 'Daily',
      'As needed': 'PRN'
    };

    return frequencyMap[medicationFrequency] || 'Daily'; // Default to Daily if not found
  };

  // Handlers for switching between search and custom medication modes
  const handleSwitchToCustomAdd = () => {
    setIsCustomMedicationAdd(true);
    addForm.setFieldsValue({ medicationName: '' });
    setSearchResults([]);
  };

  const handleSwitchToSearchAdd = () => {
    setIsCustomMedicationAdd(false);
    addForm.setFieldsValue({ medicationName: '' });
    setSearchResults([]);
  };

  const handleSwitchToCustomEdit = () => {
    setIsCustomMedicationEdit(true);
    editForm.setFieldsValue({ medicationName: '' });
    setSearchResults([]);
  };

  const handleSwitchToSearchEdit = () => {
    setIsCustomMedicationEdit(false);
    editForm.setFieldsValue({ medicationName: '' });
    setSearchResults([]);
  };

  // Helper function to parse medication name for dosage and route information
  const parseMedicationInfo = (medicationName) => {
    const name = medicationName || '';

    // Extract dosage (look for patterns like "500 MG", "1 GM", "750 mg", etc.)
    const dosageMatch = name.match(/(\d+(?:\.\d+)?)\s*(mg|MG|gm|GM|g|G|mcg|MCG|units?|UNITS?|ml|ML|mL)\b/i);
    const dosage = dosageMatch ? `${dosageMatch[1]} ${dosageMatch[2].toLowerCase()}` : '';

    // Extract route (look for common routes that match our available options)
    const routePatterns = {
      'Oral Tablet': 'Oral',
      'Oral Capsule': 'Oral',
      'Oral Solution': 'Oral',
      'Oral Suspension': 'Oral',
      'Tablet': 'Oral',
      'Capsule': 'Oral',
      'Solution': 'Oral',
      'Suspension': 'Oral',
      'Injectable': 'Intravenous',
      'Injection': 'Intravenous',
      'IV': 'Intravenous',
      'Intravenous': 'Intravenous',
      'Topical': 'Topical',
      'Cream': 'Topical',
      'Ointment': 'Topical',
      'Gel': 'Topical',
      'Ophthalmic': 'Ophthalmic',
      'Eye': 'Ophthalmic',
      'Nasal': 'Nasal',
      'Nose': 'Nasal',
      'Rectal': 'Rectal',
      'Sublingual': 'Oral',
      'Transdermal': 'Transdermal',
      'Patch': 'Transdermal',
      'Inhaled': 'Inhaled',
      'Inhalation': 'Inhaled',
      'Subcutaneous': 'Subcutaneous',
      'SubQ': 'Subcutaneous',
      'SC': 'Subcutaneous',
      'Intramuscular': 'Intramuscular',
      'IM': 'Intramuscular'
    };

    let route = '';
    for (const [pattern, routeValue] of Object.entries(routePatterns)) {
      if (name.toLowerCase().includes(pattern.toLowerCase())) {
        route = routeValue;
        break;
      }
    }

    return { dosage, route };
  };

  const handleMedicationSelect = async (value, option) => {
    try {
      // First, set the medication name immediately
      addForm.setFieldsValue({
        medicationName: value,
      });

      // Check if this is a popular medication (has fake rxcui) - skip API call
      const isPopularMedication = option.key.startsWith('popular_');
      
      let apiDosage = '';
      let apiRoute = '';
      
      if (!isPopularMedication) {
        // Try to get detailed information from the API for real medications
        try {
          const response = await axios.get(`/medications/details/${option.key}`);
          const details = response.data.data.details;
          apiDosage = details.strength;
          apiRoute = details.doseForm;
        } catch (error) {
          console.warn('Failed to fetch medication details, using parsed information instead:', error);
        }
      }

      // Parse medication name as fallback
      const parsedInfo = parseMedicationInfo(value);

      // Set form values, preferring API data but falling back to parsed data
      addForm.setFieldsValue({
        medicationName: value,
        dosage: apiDosage || parsedInfo.dosage,
        route: apiRoute || parsedInfo.route,
      });

      if (apiDosage || parsedInfo.dosage || apiRoute || parsedInfo.route) {
        message.success(t('medications.autoPopulatedSuccess'));
      }
    } catch (error) {
      console.error('Failed to fetch medication details:', error);

      // If API fails, still try to parse the medication name
      const parsedInfo = parseMedicationInfo(value);

      addForm.setFieldsValue({
        medicationName: value,
        dosage: parsedInfo.dosage,
        route: parsedInfo.route,
      });

      if (parsedInfo.dosage || parsedInfo.route) {
        message.info(t('medications.autoPopulatedPartial'));
      } else {
        message.warning(t('medications.autoPopulatedFailed'));
      }
    }
  };

  // Handler for medication selection in edit modal
  const handleEditMedicationSelect = async (value, option) => {
    try {
      // First, set the medication name immediately
      editForm.setFieldsValue({
        medicationName: value,
      });

      // Check if this is a popular medication (has fake rxcui) - skip API call
      const isPopularMedication = option.key.startsWith('popular_');
      
      let apiDosage = '';
      let apiRoute = '';
      
      if (!isPopularMedication) {
        // Try to get detailed information from the API for real medications
        try {
          const response = await axios.get(`/medications/details/${option.key}`);
          const details = response.data.data.details;
          apiDosage = details.strength;
          apiRoute = details.doseForm;
        } catch (error) {
          console.warn('Failed to fetch medication details, using parsed information instead:', error);
        }
      }

      // Parse medication name as fallback
      const parsedInfo = parseMedicationInfo(value);

      // Set form values, preferring API data but falling back to parsed data
      editForm.setFieldsValue({
        medicationName: value,
        dosage: apiDosage || parsedInfo.dosage,
        route: apiRoute || parsedInfo.route,
      });

      if (apiDosage || parsedInfo.dosage || apiRoute || parsedInfo.route) {
        message.success(t('medications.autoPopulatedSuccess'));
      }
    } catch (error) {
      console.error('Failed to fetch medication details:', error);

      // If API fails, still try to parse the medication name
      const parsedInfo = parseMedicationInfo(value);

      editForm.setFieldsValue({
        medicationName: value,
        dosage: parsedInfo.dosage,
        route: parsedInfo.route,
      });

      if (parsedInfo.dosage || parsedInfo.route) {
        message.info(t('medications.autoPopulatedPartial'));
      } else {
        message.warning(t('medications.autoPopulatedFailed'));
      }
    }
  };

  const fetchMedicationData = useCallback(async () => {
     if (!patientId) {
        setLoading(false); 
        return;
    }
    setLoading(true);
    setError(null);
    console.log("PatientMedications: Fetching data for patient:", patientId); // DEBUG
      try {
        // Request population of the user within the new review field
        const response = await axios.get(`/patients/${patientId}?populate=medicationLastReviewed.user`);
        const patientData = response?.data?.data?.patient;

        console.log("PatientMedications: Raw patient data received:", patientData); // DEBUG

        if (!patientData) {
            console.error("Error fetching patient data: Invalid data structure received", response.data);
            throw new Error("Received invalid patient data structure from server.");
        }

        // ** FIX: Declare variables before setting state to avoid temporal dead zone issues in minified code **
        const meds = patientData?.medications || [];
        const checklistData = patientData?.structuredMedicationChecklist || { items: [] };
        const lastReviewedData = patientData?.medicationLastReviewed || null;

        // Set medications
        setMedications(meds);
        console.log("PatientMedications: Medications set:", meds); // DEBUG

        // Set checklist data
        console.log("PatientMedications: Raw checklist data from patient:", checklistData); // DEBUG
        setStructuredChecklist(checklistData);
        console.log("PatientMedications: Checklist state set:", checklistData); // DEBUG

        // Set last reviewed status
        setLastReviewed(lastReviewedData);
        console.log("PatientMedications: Last reviewed set:", lastReviewedData); // DEBUG

      } catch (err) {
        console.error(`Error fetching medication data for patient ${patientId}:`, err);
        setError(err.response?.data?.message || t('medications.error'));
        setMedications([]);
        setStructuredChecklist({ items: [] });
        setLastReviewed(null); // Clear on error
      } finally {
        setLoading(false);
      }
  }, [patientId]);

  useEffect(() => {
    fetchMedicationData();
  }, [fetchMedicationData]);

  // Effect to refresh medications when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) { // Assuming refreshTrigger is a counter, check if it's been incremented
      console.log("PatientMedications: refreshTrigger changed, fetching medication data.");
      fetchMedicationData();
    }
  }, [refreshTrigger, fetchMedicationData]); // Add fetchMedicationData to dependencies

  // Effect to open modal when triggered by prop
  useEffect(() => {
    if (triggerOpenAddModal) {
      showAddModal();
      // Note: onAddModalClose will be called by handleAddCancel or after successful submission
      // to reset the trigger in PatientDetailPage
    }
  }, [triggerOpenAddModal]); // Removed showAddModal from deps to avoid loop if it's not stable

  // --- Mark as Reviewed Logic ---
  const handleMarkReviewed = async () => {
    if (!user || !user._id) {
      message.error(t('medications.cannotMarkReviewed'));
      return;
    }
    const reviewData = { user: user._id, date: new Date().toISOString() };
    setLoading(true); // Use main loading state or a dedicated one if preferred
    try {
      await axios.patch(`/patients/${patientId}`, { medicationLastReviewed: reviewData });
      message.success(t('medications.markedAsReviewed'));
      // Update local state immediately for responsiveness
      setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
    } catch (err) {
      console.error("Error marking medications as reviewed:", err);
      message.error(err.response?.data?.message || t('medications.failedToMarkReviewed'));
    } finally {
      setLoading(false);
    }
  };

  // --- Quick Status Update Logic ---
  const handleQuickStatusUpdate = async (medicationId, newStatus) => {
    if (!medicationId) {
      message.error(t('medications.validationRequired.medicationName'));
      return;
    }
    console.log(`Attempting to update status for medication ${medicationId} to ${newStatus}`);
    setIsUpdatingStatus(true);
    try {
      if (!token) {
        message.error(t('medications.validationRequired.status'));
        setIsUpdatingStatus(false);
        return;
      }
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      await axios.patch(`/medications/${medicationId}`, { status: newStatus }, config);
      message.success(t('medications.medicationUpdated'));
      fetchMedicationData(); // Refresh the list
      if (onMedicationUpdated) {
        onMedicationUpdated(); // Notify parent
      }
    } catch (err) {
      console.error(`Error updating medication status for ${medicationId}:`, err);
      message.error(err.response?.data?.message || t('medications.medicationUpdated'));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // --- Add Modal Logic ---
  const showAddModal = () => {
    addForm.resetFields();
    setIsPrnCheckedAdd(false); // Reset conditional state
    setIsOtherFrequencyAdd(false); // Reset conditional state
    setError(null);
    setAddModalVisible(true);
  };

  const handleAddCancel = () => {
    setAddModalVisible(false);
    setIsCustomMedicationAdd(false); // Reset custom medication mode
    setSearchResults([]); // Clear search results
    if (onAddModalClose) { // Call the callback to reset trigger in parent
      onAddModalClose();
    }
  };

  const handleAddMedication = async (values) => {
    console.log('Submitting new medication (raw form values):', values);
    
    // Validate Start Date and End Date
    if (values.startDate && values.endDate && moment(values.startDate).isAfter(moment(values.endDate), 'day')) {
      message.error('Start Date cannot be after End Date. Please select valid dates.');
      setAddSubmitLoading(false);
      return;
    }
    
    setAddSubmitLoading(true);
    try {
      // Construct payload carefully based on conditional fields
      const payload = {
        ...values,
        patient: patientId,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
        isPrn: values.isPrn || false, // Get from checkbox value
        prnIndication: values.isPrn ? values.prnIndication : undefined, // Only include if isPrn is true
        frequency: values.frequency === 'Other' ? values.otherFrequency : values.frequency, // Use specified frequency if 'Other'
        // Add custom medication flags
        isCustomMedication: isCustomMedicationAdd,
        medicationSource: isCustomMedicationAdd ? 'custom' : 'rxnav',
      };
      // Remove temporary fields not in the backend model
      delete payload.otherFrequency;

      console.log('Submitting new medication (constructed payload):', payload);

      const medicationResponse = await axios.post(`/patients/${patientId}/medications`, payload);
      message.success(t('medications.medicationAdded'));
      
      // Now, create the corresponding "Order"
      const newMedication = medicationResponse.data.data.medication; // Assuming this is the structure
      // Prepare dates for order - both are required by the Order model
      const currentDate = new Date();
      const defaultEndDate = new Date();
      defaultEndDate.setMonth(defaultEndDate.getMonth() + 3); // Default to 3 months from now

      const orderPayload = {
        patient: patientId,
        orderingProvider: user._id, // Assuming user is the ordering provider
        orderType: 'Medication',
        frequency: mapMedicationFrequencyToOrderFrequency(newMedication.frequency || 'Daily'),
        status: values.status || 'Pending', // Use selected status or default to Pending
        startDate: values.startDate ? values.startDate.toISOString() : currentDate.toISOString(),
        endDate: values.endDate ? values.endDate.toISOString() : defaultEndDate.toISOString(),
        medication: newMedication._id, // Top-level reference to the Medication document
        orderDetails: {
          // Using generic 'description' for a summary.
          // Full details are available by populating the 'medication' field on the Order.
          description: `${newMedication.medicationName || 'Unnamed Medication'} ${newMedication.dosage || ''} ${newMedication.route || ''} ${newMedication.frequency || ''}`.replace(/\s+/g, ' ').trim()
        }
      };

      console.log("PatientMedications: Creating Order with payload:", orderPayload); // For debugging
      await axios.post(`/patients/${patientId}/orders`, orderPayload);
      message.success(t('medications.medicationAdded'));

      setAddModalVisible(false);
      setIsCustomMedicationAdd(false); // Reset custom medication mode
      setSearchResults([]); // Clear search results
      addForm.resetFields(); // Reset form after successful submission
      fetchMedicationData(); // Refresh own medication list
      if (onMedicationAndOrderAdded) { // Notify parent to refresh orders list
        onMedicationAndOrderAdded();
      }
      if (onAddModalClose) { // Also ensure the trigger is reset
        onAddModalClose();
      }

    } catch (err) {
      console.error("Error adding medication and/or order:", err);
      const errorMsg = err.response?.data?.message || t('medications.error');
      message.error(errorMsg);
    } finally {
      setAddSubmitLoading(false);
    }
  };

  // --- Edit Modal Logic ---
  const showEditModal = (medication) => {
    setEditingMedication(medication);

    // Determine if frequency is 'Other'
    const translatedFrequencies = getTranslatedFrequencies(t);
    const standardFrequencies = translatedFrequencies.filter(f => f.value !== 'Other').map(f => f.value);
    const isOtherFreq = !standardFrequencies.includes(medication.frequency);
    setIsOtherFrequencyEdit(isOtherFreq);
    setIsPrnCheckedEdit(medication.isPrn || false);
    setIsCustomMedicationEdit(medication.isCustomMedication || false);

    editForm.setFieldsValue({
      ...medication,
      startDate: medication.startDate ? moment(medication.startDate) : null,
      endDate: medication.endDate ? moment(medication.endDate) : null,
      frequency: isOtherFreq ? 'Other' : medication.frequency, // Set dropdown to 'Other' if needed
      otherFrequency: isOtherFreq ? medication.frequency : undefined, // Populate the text input if 'Other'
      isPrn: medication.isPrn || false, // Populate checkbox
      prnIndication: medication.isPrn ? medication.prnIndication : undefined, // Populate indication if PRN
    });
    setEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingMedication(null);
    setIsCustomMedicationEdit(false); // Reset custom medication mode
    setSearchResults([]); // Clear search results
    editForm.resetFields(); // Reset form fields when canceling
  };

  const handleUpdateMedication = async (values) => {
    if (!editingMedication?._id) {
        message.error(t('medications.validationRequired.medicationName'));
        return;
    }
    
    // Validate Start Date and End Date
    if (values.startDate && values.endDate && moment(values.startDate).isAfter(moment(values.endDate), 'day')) {
      message.error('Start Date cannot be after End Date. Please select valid dates.');
      setEditSubmitLoading(false);
      return;
    }
    
    console.log('Updating medication (raw form values):', editingMedication._id, values);
    setEditSubmitLoading(true);
    try {
      // Construct payload carefully based on conditional fields
      const payload = {
        ...values,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
        isPrn: values.isPrn || false, // Get from checkbox value
        prnIndication: values.isPrn ? values.prnIndication : undefined, // Only include if isPrn is true
        frequency: values.frequency === 'Other' ? values.otherFrequency : values.frequency, // Use specified frequency if 'Other'
        // Add custom medication flags
        isCustomMedication: isCustomMedicationEdit,
        medicationSource: isCustomMedicationEdit ? 'custom' : 'rxnav',
      };
      // Remove temporary/internal fields
      delete payload.otherFrequency;
      delete payload.patient; // Should not change patient association
      delete payload.prescribingProvider; // Should not change provider this way

      console.log('Updating medication (constructed payload):', editingMedication._id, payload);

      await axios.patch(`/medications/${editingMedication._id}`, payload);
      message.success(t('medications.medicationUpdated'));
      setEditModalVisible(false);
              setEditingMedication(null);
              setIsCustomMedicationEdit(false); // Reset custom medication mode
              setSearchResults([]); // Clear search results
              editForm.resetFields(); // Reset form after successful update
              fetchMedicationData();
              if (onMedicationUpdated) {
                // console.log("PatientMedications: Calling onMedicationUpdated callback."); // DEBUG LOG REMOVED
                onMedicationUpdated(); // Notify parent that a medication was updated
              }
            } catch (err) {
      console.error("Error updating medication:", err);
      message.error(err.response?.data?.message || t('medications.error'));
    } finally {
      setEditSubmitLoading(false);
    }
  };

  // --- Delete Medication Logic ---
  const handleDeleteMedication = async (medicationId) => {
    if (!medicationId) {
      message.error(t('medications.validationRequired.medicationName'));
      return;
    }
    console.log('Attempting to delete medication:', medicationId);
    setDeleteLoading(true); // Use a specific loading state if needed, or reuse general loading
    try {
      // Ensure token is available
      if (!token) {
        message.error(t('medications.validationRequired.status'));
        setDeleteLoading(false);
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(`/medications/${medicationId}`, config); // Use the specific medication route
      message.success(t('medications.medicationDeleted'));
      fetchMedicationData(); // Refresh the list
      if (onMedicationUpdated) {
        onMedicationUpdated(); // Notify parent that a medication change occurred (deletion)
      }
    } catch (err) {
      console.error("Error deleting medication:", err);
      message.error(err.response?.data?.message || t('medications.error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // --- Checklist Modal Handling ---
  const showChecklistModal = () => {
      setIsChecklistModalVisible(true);
  };

  const handleChecklistCancel = () => {
      setIsChecklistModalVisible(false);
  };

  const handleChecklistSaveSuccess = () => {
      setIsChecklistModalVisible(false);
      fetchMedicationData(); 
  };

  // Define table columns for medications
  const columns = [
    {
      title: t('medications.tableHeaders.medication'),
      dataIndex: 'medicationName',
      key: 'medicationName',
      width: '20%',
    },
    {
      title: t('medications.tableHeaders.dosage'),
      dataIndex: 'dosage',
      key: 'dosage',
      width: '10%',
    },
    {
      title: t('medications.tableHeaders.frequency'),
      dataIndex: 'frequency',
      key: 'frequency',
      width: '15%',
      render: (frequency, record) => {
        const scheduledFrequency = frequency; // Original frequency value
        if (record.isPrn) {
          // Construct the PRN text with the new format
          const prnText = record.prnIndication ? `${t('medications.asNeeded')} for ${record.prnIndication}` : t('medications.asNeeded');
          // Check if there's a meaningful scheduled frequency to display alongside PRN
          if (scheduledFrequency && scheduledFrequency !== 'Other') { // Assuming 'Other' itself isn't displayed directly
            // Combine with a space instead of semicolon
            return `${scheduledFrequency} ${prnText}`;
          } else {
            return prnText; // Only show PRN if no scheduled frequency
          }
        }
        return scheduledFrequency; // Return the regular frequency if not PRN
      },
    },
    {
      title: t('medications.tableHeaders.route'),
      dataIndex: 'route',
      key: 'route',
       width: '10%',
    },
    {
      title: t('medications.tableHeaders.status'),
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => {
        let color = 'grey';
        if (status === 'Active') color = 'green';
        else if (status === 'On Hold') color = 'orange';
        else if (status === 'Discontinued') color = 'red';
        else if (status === 'Pending') color = 'blue';
        return <Tag color={color}>{status ? status.toUpperCase() : 'N/A'}</Tag>;
      },
       filters: [
        { text: t('medications.statusOptions.active'), value: 'Active' },
        { text: t('medications.statusOptions.discontinued'), value: 'Discontinued' },
        { text: 'On Hold', value: 'On Hold' },
        { text: t('medications.statusOptions.pending'), value: 'Pending' },
      ],
      onFilter: (value, record) => record.status && record.status.includes(value),
    },
     {
      title: t('medications.tableHeaders.startDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      width: '10%',
      render: (date) => date ? moment(date).format('DD/MM/YYYY') : 'N/A',
    },
    {
      title: t('medications.tableHeaders.endDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      width: '10%',
      render: (date) => date ? moment(date).format('DD/MM/YYYY') : 'N/A',
    },
    {
      title: t('medications.notes'),
      dataIndex: 'notes',
      key: 'notes',
      width: '15%',
      render: (notes) => (
        <div style={{ maxWidth: '150px', wordWrap: 'break-word' }}>
          {notes ? (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {notes.length > 50 ? `${notes.substring(0, 50)}...` : notes}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic', fontSize: '12px' }}>
              No notes
            </Text>
          )}
        </div>
      ),
    },
    {
      title: t('medications.tableHeaders.actions'),
      key: 'actions',
      width: '15%',
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="edit" onClick={() => showEditModal(record)} disabled={record.status === 'Discontinued'}>
              {t('medications.edit')}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="setActive" disabled={record.status === 'Active' || isUpdatingStatus}>
              <Popconfirm
                title={t('medications.confirmReactivate')}
                onConfirm={() => handleQuickStatusUpdate(record._id, 'Active')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                disabled={record.status === 'Active' || isUpdatingStatus}
              >
                {t('medications.reactivate')}
              </Popconfirm>
            </Menu.Item>
            <Menu.Item key="setOnHold" disabled={record.status === 'On Hold' || isUpdatingStatus}>
              <Popconfirm
                title="Set status to On Hold?"
                onConfirm={() => handleQuickStatusUpdate(record._id, 'On Hold')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                disabled={record.status === 'On Hold' || isUpdatingStatus}
              >
                Set On Hold
              </Popconfirm>
            </Menu.Item>
            <Menu.Item key="discontinue" disabled={record.status === 'Discontinued' || isUpdatingStatus}>
              <Popconfirm
                title={t('medications.confirmDiscontinue')}
                onConfirm={() => handleQuickStatusUpdate(record._id, 'Discontinued')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                disabled={record.status === 'Discontinued' || isUpdatingStatus}
              >
                {t('medications.discontinue')}
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <Space size="small">
            <Dropdown overlay={menu} trigger={['click']} disabled={isUpdatingStatus}>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
              />
            </Dropdown>
            <Popconfirm
              title={t('medications.confirmDelete')}
              onConfirm={() => handleDeleteMedication(record._id)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              okButtonProps={{ loading: deleteLoading }} // Show loading on confirm button
            >
              {/* Tooltip temporarily removed for debugging TS error */}
              <Button
                type="link"
                danger={true}
                size="small"
                icon={<DeleteOutlined />}
                // loading={deleteLoading && record._id === medicationToDeleteId} // Optional: More granular loading
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Display all checklist items that were selected by the user
  const displayChecklistItems = structuredChecklist?.items || [];

  // Determine if user can modify using new permission system
  const canModify = hasPermission('clinical.medications.manage');

  return (
    <>
      {error && !addModalVisible && !editModalVisible && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      {/* --- Action Buttons & Review Status --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
        <Space>
          {canModify && (
            <Button type="primary" icon={<HistoryOutlined />} onClick={showChecklistModal}>
              {t('medications.updateMedication')}
            </Button>
          )}
          {canModify && (
            <Button icon={<PlusOutlined />} onClick={showAddModal}>
              {t('medications.addNewMedication')}
            </Button>
          )}
          {canModify && (
            <Tooltip title={t('medications.markAsReviewed')}>
              <Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}>
                {t('medications.markAsReviewed')}
              </Button>
            </Tooltip>
          )}
        </Space>
        {/* Display Last Reviewed Status */}
        <div style={{ textAlign: 'right' }}>
          {lastReviewed?.date ? (
            <Text type="secondary" style={{ fontSize: '0.85em' }}>
              {t('medications.lastReviewedBy', {
                name: lastReviewed.user?.name || 'Unknown User',
                date: moment(lastReviewed.date).format('L LT')
              })}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: '0.85em' }}>{t('medications.neverReviewed')}</Text>
          )}
        </div>
      </div>

      {/* --- Medication Checklist Summary --- */}
      <Card size="small" title={t('medications.title')} style={{ marginBottom: 16, background: '#fafafa' }}>
          {loading && !error ? <Spin size="small" /> : (
              displayChecklistItems && displayChecklistItems.length > 0
                  ? <Space direction="vertical" align="start">
                      {displayChecklistItems.map(item => (
                        <Tag color="purple" key={item}>{item}</Tag>
                      ))}
                    </Space>
                  : <Text type="secondary">{t('medications.noMedications')}</Text>
          )}
      </Card>

       <Title level={5} style={{ marginTop: '20px', marginBottom: 10 }}>{t('medications.title')}</Title>

      {/* Medication List Table */}
      <Spin spinning={loading && !addModalVisible && !editModalVisible && !isChecklistModalVisible}> 
        <Table
          columns={columns}
          dataSource={medications}
          rowKey="_id"
          pagination={{ pageSize: 5, size: 'small' }}
          size="small"
          // scroll={{ x: 'max-content' }} // Removed scroll prop
        />
      </Spin>

      {/* --- Modals --- */} 
      
      {/* Add Medication Modal */}
        <Modal
        title={t('medications.addNewMedication')}
        open={addModalVisible}
        onCancel={handleAddCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
         <Spin spinning={addSubmitLoading}>
            <Form form={addForm} layout="vertical" onFinish={handleAddMedication}>
              <Form.Item
                name="medicationName"
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('medications.medicationName')}</span>
                    <Button
                      type="link"
                      size="small"
                      onClick={isCustomMedicationAdd ? handleSwitchToSearchAdd : handleSwitchToCustomAdd}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      {isCustomMedicationAdd ? t('medications.switchToSearch') : t('medications.switchToCustom')}
                    </Button>
                  </div>
                }
                rules={[{ required: true, message: t('medications.validationRequired.medicationName') }]}
              >
                {isCustomMedicationAdd ? (
                  <div>
                    <Input
                      placeholder={t('medications.customMedicationPlaceholder')}
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                      {t('medications.customMedicationNote')}
                    </div>
                  </div>
                ) : (
                  <Select
                    showSearch
                    placeholder={t('medications.searchPlaceholder')}
                    onSearch={debouncedSearch}
                    onSelect={handleMedicationSelect}
                    onFocus={handleMedicationFocus}
                    loading={isSearching}
                    filterOption={false}
                    notFoundContent={isSearching ? <Spin size="small" /> : null}
                  >
                    {searchResults.map(med => (
                      <Option key={med.rxcui} value={med.displayName}>
                        {med.displayName}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="dosage" label={t('medications.dosage')} rules={[{ required: true, message: t('medications.validationRequired.dosage') }]}>
                    <Input placeholder={t('medications.dosagePlaceholder')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="quantity" label={t('medications.quantity')} rules={[{ required: false }]}>
                    <Input type="number" placeholder={t('medications.quantityPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                    {/* Updated Frequency Dropdown */}
                    <Form.Item name="frequency" label={t('medications.frequency')} rules={[{ required: true, message: t('medications.validationRequired.frequency') }]}>
                      <Select
                        placeholder={t('medications.frequencyPlaceholder')}
                        showSearch
                        optionFilterProp="children"
                        onChange={(value) => setIsOtherFrequencyAdd(value === 'Other')}
                      >
                        {getTranslatedFrequencies(t).map(freq => (
                          <Option key={freq.value} value={freq.value}>{freq.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                 </Col>
                 <Col xs={24} sm={12}>
                  <Form.Item name="refills" label={t('medications.refills')} rules={[{ required: false }]}>
                    <Input type="number" placeholder={t('medications.refillsPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              {/* Conditional "Specify Frequency" Input */}
              {isOtherFrequencyAdd && (
                <Form.Item
                  name="otherFrequency"
                  label={t('medications.specifyFrequency')}
                  rules={[{ required: true, message: t('medications.validationRequired.frequency') }]}
                >
                  <Input placeholder={t('medications.otherFrequencyPlaceholder')} />
                </Form.Item>
              )}
               <Row gutter={16}>
                  <Col xs={24} sm={12}>
                     <Form.Item name="route" label={t('medications.route')} rules={[{ required: true, message: t('medications.validationRequired.route') }]}>
                       <Select placeholder={t('medications.routePlaceholder')} showSearch optionFilterProp="children">
                         {getTranslatedRoutes(t).map(route => (
                           <Option key={route.value} value={route.value}>{route.label}</Option>
                         ))}
                       </Select>
                     </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="status" label={t('medications.status')} initialValue="Pending" rules={[{ required: true, message: t('medications.validationRequired.status') }]}>
                      <Select
                        onChange={(value) => {
                          if (value !== 'Discontinued') {
                            addForm.setFieldsValue({ reasonForDiscontinuation: undefined });
                          }
                          // Trigger re-render for conditional field
                          addForm.setFieldsValue({ status: value });
                        }}
                      >
                        <Option value="Active">{t('medications.statusOptions.active')}</Option>
                        <Option value="Discontinued">{t('medications.statusOptions.discontinued')}</Option>
                        <Option value="On Hold">{t('medications.statusOptions.onHold')}</Option>
                        <Option value="Pending">{t('medications.statusOptions.pending')}</Option>
                      </Select>
                    </Form.Item>
                 </Col>
              </Row>
               <Row gutter={16}>
                 <Col xs={24} sm={12}>
                    <Form.Item name="startDate" label={t('medications.startDate')}>
                      <MomentDatePicker 
                        style={{ width: '100%' }} 
                        placeholder={t('medications.selectDate')}
                        disabledDate={(current) => {
                          const endDate = addForm.getFieldValue('endDate');
                          return endDate && current && current > moment(endDate).startOf('day');
                        }}
                      />
                    </Form.Item>
                 </Col>
                 <Col xs={24} sm={12}>
                    <Form.Item name="endDate" label={t('medications.endDate')}>
                      <MomentDatePicker 
                        style={{ width: '100%' }} 
                        placeholder={t('medications.selectDate')}
                        disabledDate={(current) => {
                          const startDate = addForm.getFieldValue('startDate');
                          return startDate && current && current < moment(startDate).startOf('day');
                        }}
                      />
                    </Form.Item>
                 </Col>
              </Row>
               {/* "As needed" Checkbox and Indication */}
               <Form.Item name="isPrn" valuePropName="checked">
                 <Checkbox onChange={(e) => setIsPrnCheckedAdd(e.target.checked)}>
                   {t('medications.asNeeded')}
                 </Checkbox>
               </Form.Item>
               {isPrnCheckedAdd && (
                 <Form.Item
                   name="prnIndication"
                   label={t('medications.prnIndication')}
                   rules={[{ required: true, message: t('medications.validationRequired.prnIndication') }]}
                 >
                   <Input placeholder={t('medications.prnIndicationPlaceholder')} />
                 </Form.Item>
               )}
              {/* Conditional Reason for Discontinuation */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
              >
                {({ getFieldValue }) =>
                  getFieldValue('status') === 'Discontinued' ? (
                    <Form.Item 
                      name="reasonForDiscontinuation" 
                      label="Reason for Discontinuation"
                      // Removed required rule
                    >
                      <Input />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
                <Form.Item name="notes" label={t('medications.notes')}>
                  <TextArea rows={2} />
                </Form.Item>

               <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                 <Button onClick={handleAddCancel} style={{ marginRight: 8 }}>
                  {t('medications.cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={addSubmitLoading}>
                  {t('medications.addMedication')}
                </Button>
              </Form.Item>
            </Form>
         </Spin>
      </Modal>

      {/* Edit Medication Modal */}
      <Modal
        title={t('medications.editMedication')}
        open={editModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
         <Spin spinning={editSubmitLoading}>
            <Form form={editForm} layout="vertical" onFinish={handleUpdateMedication}>
              <Form.Item
                name="medicationName"
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('medications.medicationName')}</span>
                    <Button
                      type="link"
                      size="small"
                      onClick={isCustomMedicationEdit ? handleSwitchToSearchEdit : handleSwitchToCustomEdit}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      {isCustomMedicationEdit ? t('medications.switchToSearch') : t('medications.switchToCustom')}
                    </Button>
                  </div>
                }
                rules={[{ required: true, message: t('medications.validationRequired.medicationName') }]}
              >
                {isCustomMedicationEdit ? (
                  <div>
                    <Input
                      placeholder={t('medications.customMedicationPlaceholder')}
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                      {t('medications.customMedicationNote')}
                    </div>
                  </div>
                ) : (
                  <Select
                    showSearch
                    placeholder={t('medications.searchPlaceholder')}
                    onSearch={debouncedSearch}
                    onSelect={handleEditMedicationSelect}
                    onFocus={handleMedicationFocus}
                    onChange={() => setSearchResults([])} // Clear results on selection
                    loading={isSearching}
                    filterOption={false} // Since we are searching server-side
                    notFoundContent={isSearching ? <Spin size="small" /> : null}
                  >
                    {searchResults.map(med => (
                      <Option key={med.rxcui} value={med.displayName}>
                        {med.displayName}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="dosage" label={t('medications.dosage')} rules={[{ required: true, message: t('medications.validationRequired.dosage') }]}>
                    <Input placeholder={t('medications.dosagePlaceholder')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="quantity" label={t('medications.quantity')} rules={[{ required: false }]}>
                    <Input type="number" placeholder={t('medications.quantityPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                    {/* Updated Frequency Dropdown */}
                    <Form.Item name="frequency" label={t('medications.frequency')} rules={[{ required: true, message: t('medications.validationRequired.frequency') }]}>
                      <Select
                        placeholder={t('medications.frequencyPlaceholder')}
                        showSearch
                        optionFilterProp="children"
                        onChange={(value) => setIsOtherFrequencyEdit(value === 'Other')}
                      >
                        {getTranslatedFrequencies(t).map(freq => (
                          <Option key={freq.value} value={freq.value}>{freq.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                 </Col>
                 <Col xs={24} sm={12}>
                  <Form.Item name="refills" label={t('medications.refills')} rules={[{ required: false }]}>
                    <Input type="number" placeholder={t('medications.refillsPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              {/* Conditional "Specify Frequency" Input */}
              {isOtherFrequencyEdit && (
                <Form.Item
                  name="otherFrequency"
                  label={t('medications.specifyFrequency')}
                  rules={[{ required: true, message: t('medications.validationRequired.frequency') }]}
                >
                  <Input placeholder={t('medications.otherFrequencyPlaceholder')} />
                </Form.Item>
              )}
               <Row gutter={16}>
                  <Col xs={24} sm={12}>
                     <Form.Item name="route" label={t('medications.route')} rules={[{ required: true, message: t('medications.validationRequired.route') }]}>
                       <Select placeholder={t('medications.routePlaceholder')} showSearch optionFilterProp="children">
                         {getTranslatedRoutes(t).map(route => (
                           <Option key={route.value} value={route.value}>{route.label}</Option>
                         ))}
                       </Select>
                     </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="status" label={t('medications.status')} rules={[{ required: true, message: t('medications.validationRequired.status') }]}>
                      <Select
                        onChange={(value) => {
                          if (value !== 'Discontinued') {
                            editForm.setFieldsValue({ reasonForDiscontinuation: undefined });
                          }
                           // Trigger re-render for conditional field
                          editForm.setFieldsValue({ status: value });
                        }}
                      >
                        <Option value="Active">{t('medications.statusOptions.active')}</Option>
                        <Option value="Discontinued">{t('medications.statusOptions.discontinued')}</Option>
                        <Option value="On Hold">{t('medications.statusOptions.onHold')}</Option>
                        <Option value="Pending">{t('medications.statusOptions.pending')}</Option>
                      </Select>
                    </Form.Item>
                 </Col>
              </Row>
               <Row gutter={16}>
                 <Col xs={24} sm={12}>
                    <Form.Item name="startDate" label={t('medications.startDate')}>
                      <MomentDatePicker 
                        style={{ width: '100%' }} 
                        placeholder={t('medications.selectDate')}
                        disabledDate={(current) => {
                          const endDate = editForm.getFieldValue('endDate');
                          return endDate && current && current > moment(endDate).startOf('day');
                        }}
                      />
                    </Form.Item>
                 </Col>
                 <Col xs={24} sm={12}>
                    <Form.Item name="endDate" label={t('medications.endDate')}>
                      <MomentDatePicker 
                        style={{ width: '100%' }} 
                        placeholder={t('medications.selectDate')}
                        disabledDate={(current) => {
                          const startDate = editForm.getFieldValue('startDate');
                          return startDate && current && current < moment(startDate).startOf('day');
                        }}
                      />
                    </Form.Item>
                 </Col>
              </Row>
               {/* "As needed" Checkbox and Indication */}
               <Form.Item name="isPrn" valuePropName="checked">
                 <Checkbox onChange={(e) => setIsPrnCheckedEdit(e.target.checked)}>
                   {t('medications.asNeeded')}
                 </Checkbox>
               </Form.Item>
               {isPrnCheckedEdit && (
                 <Form.Item
                   name="prnIndication"
                   label={t('medications.prnIndication')}
                   rules={[{ required: true, message: t('medications.validationRequired.prnIndication') }]}
                 >
                   <Input placeholder={t('medications.prnIndicationPlaceholder')} />
                 </Form.Item>
               )}
              {/* Conditional Reason for Discontinuation */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
              >
                {({ getFieldValue }) =>
                  getFieldValue('status') === 'Discontinued' ? (
                    <Form.Item
                      name="reasonForDiscontinuation"
                      label={t('medications.discontinue')}
                      // Removed required rule
                    >
                      <Input />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
                <Form.Item name="notes" label={t('medications.notes')}>
                  <TextArea rows={2} />
                </Form.Item>

               <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                 <Button onClick={handleEditCancel} style={{ marginRight: 8 }}>
                  {t('medications.cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={editSubmitLoading}>
                  {t('medications.updateMedication')}
                </Button>
              </Form.Item>
            </Form>
         </Spin>
      </Modal>

      {/* Medication Checklist Modal */}
      <Modal
        title={t('medications.updateMedication')}
        open={isChecklistModalVisible}
        onCancel={handleChecklistCancel}
        footer={null} 
        destroyOnClose
        width={700}
      >
        {isChecklistModalVisible && (
            <StructuredMedicationChecklistForm
                patientId={patientId}
                onSaveSuccess={handleChecklistSaveSuccess}
                onCancel={handleChecklistCancel}
            />
        )}
      </Modal>

    </>
  );
};

export default PatientMedications;
